import type { AiMcpServer } from "@repo/shared/types";
import { AlertTriangle, Loader2, Power, PowerOff, RotateCw, Trash2, Zap } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { del, get, patch, post } from "@/lib/api/_client";
import { useT } from "@/lib/i18n/use-t";
import { log } from "@/lib/logger/browser-logger";
import { T } from "./mcp-servers.i18n";

const BASE = "/internal/ai/mcp-servers";
const API_BASE = process.env.BUN_PUBLIC_API_URL;

export function McpServers() {
  const t = useT(T);
  const [servers, setServers] = useState<AiMcpServer[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  const loadServers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await get<AiMcpServer[]>(BASE);
      setServers(data);
    } catch (error) {
      log.error({ err: error }, "Failed to load MCP servers");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadServers();
  }, [loadServers]);

  // Reload when window regains focus (after OAuth redirect)
  useEffect(() => {
    const handleFocus = () => loadServers();
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [loadServers]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-xs font-medium text-muted-foreground">{t.heading}</div>
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-xs gap-1"
          onClick={() => setShowAdd(true)}
        >
          {t.add}
        </Button>
      </div>

      {showAdd && (
        <AddServerForm
          onAdded={() => {
            setShowAdd(false);
            loadServers();
          }}
          onCancel={() => setShowAdd(false)}
        />
      )}

      {loading ? (
        <div className="flex justify-center py-4">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      ) : servers.length === 0 ? (
        <p className="text-xs text-muted-foreground text-center py-4">{t.empty}</p>
      ) : (
        <div className="space-y-2">
          {servers.map((server) => (
            <ServerCard key={server.id} server={server} onChanged={loadServers} />
          ))}
        </div>
      )}
    </div>
  );
}

function AddServerForm({ onAdded, onCancel }: { onAdded: () => void; onCancel: () => void }) {
  // `tr`, not `t`: the auth-type buttons below already map over a `t`.
  const tr = useT(T);
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [authType, setAuthType] = useState<"none" | "api_key" | "oauth">("none");
  const [apiKey, setApiKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const trimmedName = name.trim();
  const trimmedUrl = url.trim();

  const handleSubmit = async () => {
    if (!trimmedName || !trimmedUrl) return;
    setSaving(true);
    setErrorMsg(null);
    try {
      await post(BASE, {
        name: trimmedName,
        url: trimmedUrl,
        authType,
        ...(authType === "api_key" && apiKey ? { apiKey } : {}),
      });
      onAdded();
    } catch (error) {
      log.error({ err: error }, "Failed to add server");
      setErrorMsg(error instanceof Error ? error.message : tr.addFailed);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border rounded-md p-3 space-y-2">
      <Input
        className="h-8 text-xs"
        placeholder={tr.serverName}
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <Input
        className="h-8 text-xs"
        placeholder={tr.urlPlaceholder}
        value={url}
        onChange={(e) => setUrl(e.target.value)}
      />
      <div className="flex gap-1">
        {(["none", "api_key", "oauth"] as const).map((t) => (
          <button
            key={t}
            type="button"
            className={`px-2 py-1 rounded text-[10px] transition-colors ${
              authType === t
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:bg-muted"
            }`}
            onClick={() => setAuthType(t)}
          >
            {t === "none" ? tr.noAuth : t === "api_key" ? tr.apiKeyLabel : tr.oauth}
          </button>
        ))}
      </div>
      {authType === "api_key" && (
        <Input
          className="h-8 text-xs"
          placeholder={tr.apiKeyPlaceholder}
          type="password"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
        />
      )}
      {authType === "oauth" && <p className="text-[10px] text-muted-foreground">{tr.oauthHint}</p>}
      {errorMsg && <p className="text-xs text-destructive">{errorMsg}</p>}
      <div className="flex gap-2">
        <Button
          size="sm"
          className="h-7 text-xs flex-1"
          onClick={handleSubmit}
          disabled={!trimmedName || !trimmedUrl}
          loading={saving}
        >
          {tr.add}
        </Button>
        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={onCancel}>
          {tr.cancel}
        </Button>
      </div>
    </div>
  );
}

function ServerCard({ server, onChanged }: { server: AiMcpServer; onChanged: () => void }) {
  const t = useT(T);
  const [testing, setTesting] = useState(false);
  // { ok } rather than matching the message text: the message is translated,
  // so startsWith("Connected") would style every Norwegian success as an error.
  const [testResult, setTestResult] = useState<{ ok: boolean; text: string } | null>(null);

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await post<{ success: boolean; toolCount?: number; error?: string }>(
        `${BASE}/${server.id}/test`,
        {},
      );
      setTestResult(
        result.success
          ? { ok: true, text: t.connected(String(result.toolCount)) }
          : { ok: false, text: `${t.failed}: ${result.error}` },
      );
    } catch (error) {
      setTestResult({ ok: false, text: `${t.error}: ${error}` });
    } finally {
      setTesting(false);
    }
  };

  const handleToggle = async () => {
    await patch(`${BASE}/${server.id}`, { enabled: !server.enabled });
    onChanged();
  };

  const handleDelete = async () => {
    await del(`${BASE}/${server.id}`);
    onChanged();
  };

  const handleOAuthConnect = () => {
    // Open OAuth flow in a popup/new tab — on completion it redirects back
    window.open(`${API_BASE}${BASE}/${server.id}/oauth/connect`, "_blank", "width=600,height=700");
  };

  const handleResetFailures = async () => {
    await post(`${BASE}/${server.id}/reset`, {});
    onChanged();
  };

  const needsOAuth = server.authType === "oauth" && server.oauthStatus !== "connected";
  const isOAuthExpired = server.authType === "oauth" && server.oauthStatus === "expired";
  const isAutoDisabled = !!server.autoDisabledAt;

  return (
    <div className="border rounded-md p-3 space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-medium truncate">{server.name}</span>
            {server.authType === "oauth" && server.oauthStatus === "connected" && (
              <Badge variant="success" className="text-[10px] px-1.5 py-0">
                OAuth
              </Badge>
            )}
          </div>
          <div className="text-[10px] text-muted-foreground truncate">{server.url}</div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={handleToggle}
            tooltip={server.enabled ? t.disable : t.enable}
          >
            {server.enabled ? (
              <Power className="h-3.5 w-3.5 text-success" />
            ) : (
              <PowerOff className="h-3.5 w-3.5 text-muted-foreground" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={handleTest}
            tooltip={t.testConnection}
            loading={testing}
          >
            <Zap className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={handleDelete}
            tooltip={t.delete}
          >
            <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
          </Button>
        </div>
      </div>

      {/* Auto-disabled banner — appears when N consecutive probes failed */}
      {isAutoDisabled && (
        <div className="flex items-center gap-1.5 text-[11px] bg-warning/10 border border-warning/40 rounded px-2 py-1.5">
          <AlertTriangle className="h-3 w-3 flex-shrink-0 text-warning" />
          <span className="flex-1 min-w-0">
            Auto-disabled after {server.consecutiveFailures} failed checks.
            {server.authType === "oauth" && " Reconnect OAuth and"} re-enable to retry.
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5 flex-shrink-0"
            onClick={handleResetFailures}
            tooltip={t.resetFailures}
          >
            <RotateCw className="h-3 w-3" />
          </Button>
        </div>
      )}

      {/* OAuth connect/reconnect button */}
      {needsOAuth && (
        <Button
          variant={isOAuthExpired ? "outline" : "default"}
          size="sm"
          className="w-full h-7 text-xs gap-1.5"
          onClick={handleOAuthConnect}
        >
          {isOAuthExpired ? t.reconnectOAuth : t.connectOAuth}
        </Button>
      )}

      {testResult && (
        <div className={`text-[11px] ${testResult.ok ? "text-success" : "text-destructive"}`}>
          {testResult.text}
        </div>
      )}
    </div>
  );
}
